import * as React from 'react';
import { visualize, story, snap } from 'storybook-snapper';
import { Range, RangeProps } from '..';
import { TPAComponentsProvider } from '../TPAComponentsConfig';
import { classes } from './Range.visual.st.css';
import { ErrorAppearance } from '../internal/ErrorMessageWrapper';
import { VisualAsync } from '../../test/visual/VisualAsync';
import { rangeDriverFactory } from './Range.driver';

const commonProps: RangeProps = {
  max: 100,
  label: 'Range Component',
  value: [30, 70],
};

const errorMessage = 'This is an error message';
const getElementFromBelow = () => {
  return (
    <div className={classes.elementBelow}>
      An element below to show the spacing
    </div>
  );
};

visualize('Range', () => {
  story('simple', () => {
    snap('default props', <Range max={100} />);
    snap('show label', <Range {...commonProps} label="This is a label" />);
    snap('show value undefined', <Range {...commonProps} value={undefined} />);
    snap('show value 0-100', <Range {...commonProps} value={[0, 100]} />);
    snap('show value 0-50', <Range {...commonProps} value={[0, 50]} />);
    snap('show value 50-100', <Range {...commonProps} value={[50, 100]} />);
    snap('show value 35-75', <Range {...commonProps} value={[35, 75]} />);
    snap(
      'show currency',
      <Range
        {...commonProps}
        value={[35, 75]}
        format={{ style: 'currency', currency: 'USD' }}
      />,
    );
    snap('show disabled state', <Range {...commonProps} disabled />);
    snap(
      'rtl',
      <div dir="rtl">
        <TPAComponentsProvider value={{ rtl: true }}>
          <Range {...commonProps} value={[35, 75]} />
        </TPAComponentsProvider>
      </div>,
    );
    snap(
      'Error',
      <div>
        <Range {...commonProps} errorMessage={errorMessage} />
        {getElementFromBelow()}
      </div>,
    );
    snap(
      'Error / Appearance - BackgroundAndBorder',
      <div>
        <Range
          {...commonProps}
          errorMessage={errorMessage}
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
        />
        {getElementFromBelow()}
      </div>,
    );
  });
  story('simple / focus', () => {
    const its = [
      { it: 'default', props: { ...commonProps } },
      { it: 'disabled', props: { ...commonProps, disabled: true } },
    ];
    its.forEach(({ it, props }) => {
      snap(`${it}: min`, (done) => (
        <VisualAsync
          driverFactory={rangeDriverFactory}
          onDone={done}
          action={(driver) =>
            driver
              .handle(0)
              .getNative()
              .then((el) => el.focus())
          }
        >
          <Range {...props} />
        </VisualAsync>
      ));
      snap(`${it}: max`, (done) => (
        <VisualAsync
          driverFactory={rangeDriverFactory}
          onDone={done}
          action={(driver) =>
            driver
              .handle(1)
              .getNative()
              .then((el) => el.focus())
          }
        >
          <Range {...props} />
        </VisualAsync>
      ));
    });
  });
  story('styleOverride', () => {
    snap(
      'TrackColor',
      <Range {...commonProps} className={classes.trackColor} />,
    );
    snap(
      'TrackCornerRadius',
      <Range {...commonProps} className={classes.trackCornerRadius} />,
    );
    snap(
      'SelectedColor',
      <Range {...commonProps} className={classes.selectedColor} />,
    );
    snap('TextFont', <Range {...commonProps} className={classes.textFont} />);
    snap('TextColor', <Range {...commonProps} className={classes.textColor} />);
    snap('LabelFont', <Range {...commonProps} className={classes.labelFont} />);
    snap(
      'LabelColor',
      <Range {...commonProps} className={classes.labelColor} />,
    );
    snap(
      'Error with override styles',
      <div>
        <Range
          {...commonProps}
          errorMessage={errorMessage}
          className={classes.root}
        />
        {getElementFromBelow()}
      </div>,
    );
  });

  story('wst theming', () => {
    const style = {
      '--wst-paragraph-2-color-rgb': '198,0,0',
      '--wst-paragraph-2-font-family': 'Courier New, serif',
      '--wst-links-and-actions-color-rgb': '150,100,50',
      '--wst-paragraph-2-font-size': '22px',
      '--wst-paragraph-2-font-weight': 'bold',
      '--wst-paragraph-2-font-style': 'italic',
      '--wst-paragraph-2-font-variant': 'small-caps',
    } as React.CSSProperties;

    snap(
      'default',
      <div style={style}>
        <Range {...commonProps} />
      </div>,
    );

    snap('disabled state', () => {
      const style = {
        '--wst-system-disabled-color-rgb': '100,50,100',
      } as React.CSSProperties;

      return (
        <div style={style}>
          <Range {...commonProps} disabled />
        </div>
      );
    });
    snap('error state', () => {
      const style = {
        '--wst-system-error-color-rgb': '50,100,150',
      } as React.CSSProperties;

      return (
        <div style={style}>
          <Range
            {...commonProps}
            errorMessage="Required Field"
            errorAppearance={ErrorAppearance.BackgroundAndBorder}
          />
        </div>
      );
    });
  });
});
